import { useMemo } from "react";
import {
	fileStatus,
	fileTypes,
	IAddStudyData,
	ICollaboratorData,
	IFilterStudiesProps,
	IMetaDetails,
	IStudiesCount,
	IStudyData,
	ITableProps,
	SortBy,
	SortOrder,
	StudyPageTabType,
} from "../models/interfaces";
import { AskKuralStudyTypeMapping } from "src/pages/NewAskKural/models/interfaces";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/en";
import "dayjs/locale/hi";
import "dayjs/locale/ja";
import "dayjs/locale/zh-cn";
import i18n from "src/i18n";
import { storyType } from "src/pages/Studies/models/interfaces";
import { environment } from "src/config/environment";
import pxbIconSvg from "public/icon/pxb_icon.svg?url";
import simulationMessageTestingIconSvg from "public/icon/simulation_message_testing_icon.svg?url";

// Color tokens for study-type icons (primary vs. secondary surfaces).
const STUDY_TYPE_ICON_PRIMARY_COLOR = "var(--primary-color)";
const STUDY_TYPE_ICON_SECONDARY_COLOR = "var(--secondary-color)";

// Rendered size (square, in px) per surface.
const STUDY_TYPE_ICON_PRIMARY_SIZE = 40;
const STUDY_TYPE_ICON_SECONDARY_SIZE = 18;

// Serializable descriptor for a study-type icon. Stored in Redux state, so it
// must contain only primitives (no JSX / React elements / functions).
export type StudyTypeIconDescriptor = {
	type: "material" | "custom";
	// For 'material': the Google Material Symbol name (e.g. "voice_chat").
	// For 'custom': the resolved URL of the SVG asset.
	value: string;
	// Color to render the icon with. Primary vs. secondary surface.
	color: string;
	// Rendered size in px (applied as both width and height).
	size: number;
};

// Studies whose icon is a Google Material Symbol (Outlined). Keys are the
// normalized study-type label (lowercase, non-alnum stripped, spaces -> '_')
// plus the `_icon` suffix that the legacy PNG naming used.
const MATERIAL_SYMBOL_BY_LABEL: Record<string, string> = {
	market_research_icon: "voice_chat",
	notes_icon: "note_stack",
	quant_icon: "finance_mode",
	reports_icon: "document_search",
	web_boards_icon: "computer_sound",
};

// Studies whose icon is a brand-specific custom SVG asset.
const CUSTOM_SVG_BY_LABEL: Record<string, string> = {
	pxb_icon: pxbIconSvg,
	simulation_message_testing_icon: simulationMessageTestingIconSvg,
};

const normalizeStudyTypeLabel = (studyTypeName: string): string => {
	return studyTypeName
		.toLowerCase()
		.replace(/[^a-zA-Z0-9-_ ]/g, "")
		.replace(/[\s-]+/g, "_");
};

const resolveStudyTypeIcon = (studyTypeName: string, color: string, size: number): StudyTypeIconDescriptor | null => {
	if (!studyTypeName) return null;

	const processedLabel = normalizeStudyTypeLabel(studyTypeName);
	if (!processedLabel || !/^[a-z0-9_]+$/.test(processedLabel)) return null;

	const key = processedLabel.endsWith("_icon") ? processedLabel : `${processedLabel}_icon`;

	if (MATERIAL_SYMBOL_BY_LABEL[key]) {
		return { type: "material", value: MATERIAL_SYMBOL_BY_LABEL[key], color, size };
	}
	if (CUSTOM_SVG_BY_LABEL[key]) {
		return { type: "custom", value: CUSTOM_SVG_BY_LABEL[key], color, size };
	}
	return null;
};

// Map i18n language codes to dayjs locale codes
const localeMap = {
	zh: "zh-cn",
	hi: "hi",
	ja: "ja",
	en: "en",
};

export const transformFormValues = (values: any, metaDetails?: IMetaDetails): IAddStudyData => {
	const { date_range, fieldwork_date, study_language, ...rest } = values;
	const { end_date, start_date } = extractDateRange(date_range || fieldwork_date);

	let transformedStudyLanguage = study_language;

	// If metaDetails and userProfile are provided, return the whole language object in study_language
	if (metaDetails && study_language) {
		const langObj = metaDetails?.languages?.find((lang) => lang.code === study_language);
		if (langObj) transformedStudyLanguage = langObj;
	}

	return {
		...rest,
		...(start_date && { start_date }),
		...(end_date && { end_date }),
		...(transformedStudyLanguage && { study_language: transformedStudyLanguage }),
	};
};

export const getTopTags = (studies: IStudyData[]): string[] => {
	// Step 1: Extract and count occurrences of each tag in one pass
	const tagCounts = (studies || []).reduce<Record<string, number>>((acc, study) => {
		(study?.custom_tags || []).forEach((tag) => {
			if (tag) {
				acc[tag] = (acc[tag] || 0) + 1;
			}
		});
		return acc;
	}, {});

	// Step 2: Sort tags by occurrences and select the top 5
	const topTags = Object.entries(tagCounts)
		.sort(([, countA], [, countB]) => countB - countA)
		.map(([tag]) => tag);

	// Step 3: Add 'All' tag at the beginning
	return ["All", ...topTags];
};

export const getInitials = (name: string | null): string => {
	if (!name) return "";
	const words = name.split(" ").filter((word) => word);
	if (words.length < 2) return words[0]?.charAt(0).toUpperCase() || "";
	return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

export const getNameFromEmail = (email: string | null): string => {
	if (!email) return "";
	const namePart = email.split("@")[0];
	const words = namePart.split(".").filter((word) => word);
	if (words.length === 1) {
		const singleWord = words[0];
		return singleWord.charAt(0).toUpperCase() + singleWord.slice(1);
	}
	if (words.length < 2) return "";
	const firstName = words[0].charAt(0).toUpperCase() + words[0].slice(1);
	const lastName = words[words.length - 1].charAt(0).toUpperCase() + words[words.length - 1].slice(1);
	return `${firstName} ${lastName}`;
};

export function transformCustomTags(tags: { label: string; value: string }[] | null): { label: string; value: string }[] {
	return (tags || []).map((tag) => ({
		...tag,
		value: tag.label || "",
	}));
}

export function transformCollaborators(collaborators: ICollaboratorData[]) {
	return collaborators.map((collaborator) => ({
		value: collaborator.collaborator_id ?? "",
		label: collaborator.collaborator_name ?? "",
		email: collaborator.collaborator_email ?? "",
		user_access_type: collaborator.user_access_type ?? "Read Access",
	}));
}

export const checkAccessType = (type) => {
	if (type === "Read Access") {
		return "View";
	} else if (type === "Full Access") {
		return "Edit";
	}
	return "Access Required";
};

export function transformPayload(input: any[]): ITableProps[] {
	return input.map((item) => {
		const baseOutput: ITableProps = {
			id: item._id,
			uploaded_files: item.filename,
			uploaded_by: item.uploaded_by?.full_name,
			uploaded_date: item.upload_dt,
			status: item.processing_status,
			type: item.document_type,
			status_log: item.display_on_ui,
			file_id: item.file_id,
		};

		if (item.transcripts_metadata) {
			Object.entries(item.transcripts_metadata).forEach(([key, value]) => {
				const formattedKey = key.toLowerCase().replace(/\s+/g, "_");
				baseOutput[formattedKey] = typeof value === "string" ? value : JSON.stringify(value);
			});
		}

		return baseOutput;
	});
}

export const transformNameWithDateTime = (uploadedBy, uploadDate, t) => {
	// Extend dayjs with required plugins
	dayjs.extend(relativeTime);
	dayjs.extend(utc);
	dayjs.locale(localeMap[i18n.language] || "en");

	if (!uploadDate) return "";

	const formattedDate = uploadDate ? dayjs.utc(uploadDate).local().fromNow() : "";
	const byText = t("by");
	const unknownUser = t("Unknown User");
	const formattedText = `${formattedDate} ${byText} ${uploadedBy || unknownUser}`;
	return formattedText;
};

export const transformDateTimeCapitalize = (uploadedBy, uploadDate, t) => {
	// Extend dayjs with required plugins
	dayjs.extend(relativeTime);
	dayjs.extend(utc);
	dayjs.locale(localeMap[i18n.language] || "en");

	if (!uploadDate) return "";

	const formattedDate = uploadDate ? dayjs.utc(uploadDate).local().fromNow() : "";
	return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
};

export const transformName = (uploadedBy, t) => {
	if (!uploadedBy) return "";

	const byText = t("by");
	const unknownUser = t("Unknown User");
	const formattedText = `${byText} ${uploadedBy || unknownUser}`;
	return formattedText;
};

export const transformDateTime = (uploadDate) => {
	dayjs.extend(relativeTime);
	dayjs.extend(utc);

	const locale = localeMap[i18n.language] || "en";

	dayjs.locale(locale);
	return uploadDate ? `${dayjs.utc(uploadDate).local().fromNow()}` : "";
};

export const truncateFileName = (entityName: string, length: number = 60) => {
	return entityName?.length > length ? entityName.substring(0, length) : entityName;
};

export const generateFileName = (fileType: string, entityName: string, ques_id?: string | number, downloadFormat?: string) => {
	const entityLastDotIndex = entityName?.lastIndexOf(".");
	const truncatedFileName = truncateFileName(entityLastDotIndex && entityLastDotIndex > 0 ? entityName.substring(0, entityLastDotIndex) : entityName || "");
	const instanceName = (environment.REACT_APP_INSTANCE_NAME || "QualAI_Kural").replace("_", " ");

	switch (fileType) {
		case fileTypes.discussion_guide:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Discussion Guide.docx")}`;
		case fileTypes.transcript:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Transcript.docx")}`;
		case fileTypes.report:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Report")}.${downloadFormat}`;
		case fileTypes.all:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Study Data.zip")}`;
		case fileTypes.metadata:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Metadata Template.xlsx")}`;
		case fileTypes.allInsights:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Insights Deep Dive.xlsx")}`;
		case fileTypes.insightsQuestion:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Insights - Question")} ${ques_id}.xlsx`;
		case fileTypes.errorlogs:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Logs")}.${downloadFormat}`;
		case fileTypes.notes:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Notes")}.${downloadFormat}`;
		case fileTypes.rawData:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("Raw Data")}.${downloadFormat}`;
		case fileTypes.addUsers:
			return `${instanceName} - Add User(s) Template.csv`;
		case fileTypes.addTaxonomy:
			return `${instanceName} - Add Taxonomy Template.xlsx`;
		case fileTypes.logs:
			return `${instanceName} - Event Logs.csv`;
		default:
			return `${truncatedFileName} - ${i18n.t(instanceName)} ${i18n.t("File")}.${downloadFormat ?? ""}`;
	}
};

export const validateLanguageInput = (supportedLanguages: string[], value: string, allowedSpecialChars: string): string | null => {
	if (!value) {
		return null; // Empty values are valid
	}
	// Check for blank/space-only input
	if (!value.trim()) {
		return "Input cannot be blank or spaces only";
	}
	// Language-specific rules
	const languageRules: Record<string, string> = {
		hi: "\\u0900-\\u097F।.", // Hindi (Devanagari + danda + period)
		ja: "\\u3040-\\u30FF\\u4E00-\\u9FAF。．.？「」、", // Japanese (hiragana, katakana, kanji, Japanese fullstop, fullwidth period, ASCII period, question marks, brackets)
		zh: "\\u4E00-\\u9FFF。．.？", // Mandarin (CJK, Chinese fullstop, fullwidth period, ASCII period)
		de: "a-zA-ZäöüÄÖÜß.", // German (letters + period)
		en: "a-zA-Z0-9.", // English (alphanumeric + period)
	};

	// Escape special characters for regex
	const escapedSpecialChars = allowedSpecialChars.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

	// Build regex for supported languages
	const combinedRegexSource = supportedLanguages
		.map((lang) => languageRules[lang] || "") // Get the rules for each language
		.filter((rule) => rule) // Remove empty rules (unsupported languages)
		.join(""); // Combine into a single regex source

	if (!combinedRegexSource) {
		return "Unsupported language.";
	}

	// Final regex: Combine language rules and allowed special characters.
	// Safe: pattern is assembled from static, code-defined language rules and an
	// escaped static char set (not user input); an anchored char class with no
	// backtracking risk — the user value is only the test target, not the pattern.
	const finalRegex = new RegExp(`^[${combinedRegexSource}\\s${escapedSpecialChars}]+$`, "i");

	// Validate the input value
	if (!finalRegex.test(value)) {
		return "Invalid language or special characters. Allowed: ";
	}

	return null; // Input is valid
};

export const getStudyTypeDisplayName = (studyTypeKey: string, allStudyTypes) => {
	const studyType = allStudyTypes?.find((type) => type?.key === studyTypeKey || type?.label === studyTypeKey || type?.display_name === studyTypeKey);
	return studyType ? studyType?.display_name || studyType?.label : "";
};

export const getAskKuralStudyTypeDisplayName = (askKuralKey: string, allStudyTypes) => {
	const studyType = allStudyTypes?.find((type) => AskKuralStudyTypeMapping[type?.key] === askKuralKey);
	return studyType ? studyType?.ask_kural_display_name : askKuralKey;
};

export const getFeatureKey = (label: string, allStudyTypes) => {
	const studyType = allStudyTypes?.find((type) => type?.label === label || type?.key === label || type?.display_name === label);
	return studyType ? studyType?.key : "";
};

// Build-time static context of study-type icons. Webpack resolves this to a fixed set of
// modules matching `<name>_icon.png` in public/icon, so lookups can never reach an
// arbitrary path (avoids the non-literal `require()` pattern).
const iconContext = require.context("../../../../public/icon", false, /_icon\.png$/);

// Returns a serializable descriptor for a study type's primary icon (color #112E65).
// Render with the `<StudyTypeIcon />` component.
export const processIconPath = (studyTypeName: string): StudyTypeIconDescriptor | null => {
	return resolveStudyTypeIcon(studyTypeName, STUDY_TYPE_ICON_PRIMARY_COLOR, STUDY_TYPE_ICON_PRIMARY_SIZE);
};

// Returns a serializable descriptor for a study type's secondary icon (color #32A29B).
// Used on AddStudy / ManageStudy surfaces. Render with the `<StudyTypeIcon />` component.
export const processIconPath2 = (studyTypeName: string): StudyTypeIconDescriptor | null => {
	return resolveStudyTypeIcon(studyTypeName, STUDY_TYPE_ICON_SECONDARY_COLOR, STUDY_TYPE_ICON_SECONDARY_SIZE);
};

export const getAvailableStudyTypes = (allStudyTypes: any[]) => {
	return allStudyTypes?.filter((studyType) => !studyType.metadata.is_hidden);
};

// The studies API may return either a flat array of studies or a { folders, untracked } envelope.
// Normalize both shapes to a flat IStudyData[] so downstream consumers can rely on array methods.
export const normalizeStudiesResponse = (data: any): IStudyData[] => {
	if (Array.isArray(data)) return data;
	if (!data) return [];
	const folderStudies = (data.folders ?? []).flatMap((folder: any) => folder.studies || []);
	return [...folderStudies, ...(data.untracked ?? [])];
};

export const useFilterStudies = ({
	metaDetails,
	searchText,
	selectedCollaborators,
	selectedStudyPageTab,
	selectedTag,
	studiesData,
}: IFilterStudiesProps & {
	selectedStudyPageTab: StudyPageTabType;
	metaDetails: IMetaDetails;
}) => {
	// Create a searchIndex for optimized text searching
	const searchIndex = useMemo(() => {
		return studiesData?.map((study) => ({
			study,
			searchableText: [
				study.study_name.toLowerCase(),
				...(study.custom_tags?.map((tag) => tag.toLowerCase()) || []),
				...(study.collaborators?.map((collab) => collab.collaborator_name.toLowerCase()) || []),
			].join(" "),
		}));
	}, [studiesData]);

	// Filter by search text, collaborators, and tags
	const filteredStudies = useMemo(() => {
		// Early return if no filters applied
		if (!searchText && (!selectedCollaborators || selectedCollaborators.length === 0) && selectedTag === "All") {
			return studiesData;
		}

		let results: IStudyData[] = studiesData;

		// Step 1: Filter by search text
		if (searchText) {
			const searchQueries = searchText
				.split(";")
				.map((query) => query.trim().toLowerCase())
				.filter((query) => query.length > 0);

			if (searchQueries.length > 0) {
				results = searchIndex.filter(({ searchableText }) => searchQueries.some((query) => searchableText.includes(query))).map(({ study }) => study);
			}
		}

		// Step 2: Filter by collaborators
		if (selectedCollaborators?.length > 0) {
			const lowerCaseSelectedCollaborators = selectedCollaborators.map((collab) => collab.collaborator.toLowerCase());

			results = results.filter((study) => study.collaborators.some((collaborator) => lowerCaseSelectedCollaborators.includes(collaborator.collaborator_name.toLowerCase())));
		}

		// Step 3: Filter by tag
		if (selectedTag !== "All") {
			results = results.filter((study) => study.custom_tags?.includes(selectedTag));
		}

		return results;
	}, [studiesData, searchText, selectedCollaborators, selectedTag, searchIndex]);

	const finalFilteredStudies = filteredStudies;

	return {
		filteredStudies,
		finalFilteredStudies,
	};
};

export const mapStatusToEnum = (status: string): fileStatus => {
	switch (status?.toLowerCase()) {
		case "queued":
			return fileStatus.queued;
		case "uploading":
			return fileStatus.uploading;
		case "upload_failed":
			return fileStatus.upload_failed;
		case "processing":
			return fileStatus.processing;
		case "success":
			return fileStatus.success;
		case "failed":
			return fileStatus.failed;
		case "validation_failed":
			return fileStatus.validation_failed;
		case "reupload_failed":
			return fileStatus.reupload_failed;
		case "partial_processed":
			return fileStatus.partial_processed;
		case "uploaded":
			return fileStatus.uploaded;
		case "warn":
			return fileStatus.warn;
		default:
			return fileStatus.failed;
	}
};

const extractDateRange = (range?: [string, string]) => {
	if (Array.isArray(range) && range.length === 2) {
		const [start, end] = range;
		return {
			start_date: start ? dayjs(start).format("YYYY-MM-DD HH:mm:ss") : undefined,
			end_date: end ? dayjs(end).format("YYYY-MM-DD HH:mm:ss") : undefined,
		};
	}
	return { start_date: undefined, end_date: undefined };
};

export const isNotesLikeStudy = (type: storyType) => {
	return type === storyType.type3 || type === storyType.type6;
};

export const formatDeleteMessage = (message: string, studiesCount: IStudiesCount, t: (key: string) => string, featureName: string): string => {
	if (!message) return "";

	const totalStudies = studiesCount.total_studies_count;
	const ownedStudies = studiesCount.studies_with_edit_access;
	const accessOnlyStudies = totalStudies - ownedStudies;

	// Helper function to get plural form
	const getPluralKey = (count: number): string => {
		return count === 1 ? "study" : "studies";
	};

	// First, translate the message template
	let translatedMessage = t(message);

	// Then, manual replacement with actual values
	translatedMessage = translatedMessage
		.replace(/{featureName}/g, t(featureName))
		.replace(/{total_studies_count}/g, totalStudies.toString())
		.replace(/{total_studies_label}/g, t(getPluralKey(totalStudies)))
		.replace(/{studies_with_edit_access}/g, ownedStudies.toString())
		.replace(/{studies_with_edit_access_label}/g, t(getPluralKey(ownedStudies)))
		.replace(/{studies_without_edit_access}/g, accessOnlyStudies.toString())
		.replace(/{studies_without_edit_access_label}/g, t(getPluralKey(accessOnlyStudies)));

	return translatedMessage;
};

export const formatMessage = (message: string, t: (key: string) => string, featureName: string): string => {
	if (!message) return "";

	const translatedMessage = t(message);
	const translatedFeatureName = t(featureName);

	return translatedMessage.replace(/{featureName}/g, translatedFeatureName);
};

export const sortStudiesLocally = (studies: IStudyData[], sortBy: SortBy, sortOrder: SortOrder): IStudyData[] => {
	return [...studies].sort((a, b) => {
		let comparison = 0;

		if (sortBy === "name") {
			comparison = a.study_name.localeCompare(b.study_name);
		} else if (sortBy === "updated_at") {
			const dateA = new Date(a.version_history?.modified_dt || 0);
			const dateB = new Date(b.version_history?.modified_dt || 0);

			// Check if dates are valid before comparing
			const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
			const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();

			comparison = timeA - timeB;
		}

		return sortOrder === "asc" ? comparison : -comparison;
	});
};
